import { useMemo } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { HiChevronDown } from 'react-icons/hi'
import {
  MenuListComponentProps,
  MultiValueProps,
  OptionProps,
  SingleValueProps,
  ValueContainerProps,
  components,
} from 'react-select'

import { Checkbox, Flex, Icon, Text } from '@chakra-ui/react'

import { FacilityMatchedFieldT, OptionI } from '@/utils/forms/optionBuilders'

import { SelectChip } from './SelectChip'
import { MenuListSelectIProps } from './types'

export const DropdownIndicator = (props: any) => {
  const mode = useLightMode()
  return (
    <components.DropdownIndicator {...props}>
      <Icon
        as={HiChevronDown}
        boxSize='20px'
        color={
          props.isDisabled ? `text.input-disabled.${mode}` : 'currentColor'
        }
      />
    </components.DropdownIndicator>
  )
}

// Custom Placeholder that return null.
// We use a custom ValueContainer to render the placeholder
export const HiddenPlaceholder = () => null

// Custom SingleValue component that renders a chip
export const SingleValue = (props: SingleValueProps<OptionI>) => {
  const { data, selectProps } = props
  const customProps = selectProps as {
    chipBgColor: string
    chipTextColor: string
    canRemoveSelectedOption: boolean
    onRemoveChip: (value: string) => void
  }

  return (
    <Flex alignItems='center' gap='8px'>
      <SelectChip
        bgColor={customProps.chipBgColor}
        label={data.label}
        onRemove={() => customProps.onRemoveChip(data.value)}
        showIcon={customProps.canRemoveSelectedOption}
        textColor={customProps.chipTextColor}
      />
    </Flex>
  )
}

// Custom MultiValue component that renders chips
export const MultiValue = (props: MultiValueProps<OptionI>) => {
  const { data, selectProps } = props
  const customProps = selectProps as {
    chipBgColor: string
    chipTextColor: string
    canRemoveSelectedOption: boolean
    onRemoveChip: (value: string) => void
    showAllSelectedChip?: boolean
    allSelectedLabel?: string
    options: OptionI[]
    value: OptionI[]
    onClearAll?: () => void
  }

  const {
    showAllSelectedChip: shouldShowAllSelectedChip,
    allSelectedLabel,
    options,
    value,
    onClearAll,
  } = customProps

  const isAllSelected =
    shouldShowAllSelectedChip &&
    value?.length === options?.length &&
    options?.length > 0

  // If all selected and showAllSelectedChip is enabled
  if (isAllSelected) {
    // Only render the "All" chip for the first selected item
    const isFirstItem = value?.[0]?.value === data.value
    if (isFirstItem) {
      return (
        <SelectChip
          bgColor={customProps.chipBgColor}
          label={allSelectedLabel}
          onRemove={() => onClearAll?.()}
          showIcon={customProps.canRemoveSelectedOption}
          textColor={customProps.chipTextColor}
        />
      )
    }
    // Don't render other chips when showing "All"
    return null
  }

  return (
    <SelectChip
      bgColor={customProps.chipBgColor}
      label={data.label}
      onRemove={() => customProps.onRemoveChip(data.value)}
      showIcon={customProps.canRemoveSelectedOption}
      textColor={customProps.chipTextColor}
    />
  )
}

// Custom ValueContainer that adds selectedLabel for multi-select
export const ValueContainer = (
  props: ValueContainerProps<OptionI, boolean>
) => {
  const mode = useLightMode()
  const { children, hasValue, selectProps } = props

  const color = selectProps.isDisabled
    ? // Disabled style
      `text.input-disabled.${mode}`
    : hasValue
    ? // Normal style
      `text.primary.${mode}`
    : // Placeholder style
      `text.input-value.${mode}`

  return (
    <components.ValueContainer {...props}>
      <Flex alignItems='center' flexWrap='wrap' gap='8px'>
        {selectProps?.selectedLabel && (
          <Text color={color} fontSize='14px' fontWeight='400'>
            {hasValue ? selectProps?.selectedLabel : selectProps?.placeholder}
          </Text>
        )}
        {children}
      </Flex>
    </components.ValueContainer>
  )
}

// Custom Option component with checkbox
export const CheckboxOption = (props: OptionProps<OptionI, boolean>) => {
  const mode = useLightMode()
  const { isSelected, isDisabled, data, innerRef, innerProps, selectProps } =
    props

  // Access custom props for subtitle display
  const customProps = selectProps as { showSubtitle?: boolean }
  const extendedData = data as OptionI & { subtitle?: string }
  const hasSubtitle = customProps.showSubtitle && extendedData.subtitle

  return (
    <Flex
      ref={innerRef}
      {...innerProps}
      _hover={isDisabled ? undefined : { bg: `border.input-hover.${mode}` }}
      alignItems={hasSubtitle ? 'flex-start' : 'center'}
      borderRadius='4px'
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      gap='12px'
      px='12px'
      py='8px'
    >
      <Checkbox
        borderColor={`text.secondary.${mode}`}
        colorScheme='checkbox-selected'
        isChecked={isSelected}
        isDisabled={isDisabled}
        mt={hasSubtitle ? '2px' : '0'}
        pointerEvents='none'
      />

      <Flex direction='column' gap='2px'>
        <Text
          color={
            isDisabled ? `text.input-disabled.${mode}` : `text.primary.${mode}`
          }
          fontSize='14px'
          fontWeight='500'
        >
          {data.label}
        </Text>
        {hasSubtitle && (
          <Text
            color={
              isDisabled
                ? `text.input-disabled.${mode}`
                : `text.secondary.${mode}`
            }
            fontSize='12px'
            fontWeight='400'
          >
            {extendedData.subtitle}
          </Text>
        )}
      </Flex>
    </Flex>
  )
}

// Custom Option component with subtitle (no checkbox)
export const CustomOption = (props: OptionProps<OptionI, boolean>) => {
  const mode = useLightMode()
  const { t } = useTranslation()
  const { data, isDisabled, selectProps } = props
  // Access custom props for subtitle display
  const customProps = selectProps as unknown as { showSubtitle?: boolean }
  const extendedData = data as OptionI & {
    subtitle?: string
    matchedField?: FacilityMatchedFieldT
  }
  const hasSubtitle = customProps.showSubtitle && extendedData.subtitle

  return (
    <components.Option {...props}>
      <Text
        color={
          isDisabled ? `text.input-disabled.${mode}` : `text.primary.${mode}`
        }
        fontSize='14px'
        fontWeight='400'
      >
        {data.label}
      </Text>
      {hasSubtitle && (
        <Text
          color={
            isDisabled
              ? `text.input-disabled.${mode}`
              : `text.secondary.${mode}`
          }
          fontSize='12px'
          fontWeight='400'
        >
          {extendedData.subtitle}
        </Text>
      )}
      {extendedData.matchedField === 'address' && (
        <Text
          color={`text.tertiary.${mode}`}
          fontSize='11px'
          fontStyle='italic'
        >
          {t('common:facility_matched_by_address')}
        </Text>
      )}
    </components.Option>
  )
}

// Custom MenuList with Select All option
export const MenuListWithSelectAll = (
  props: MenuListComponentProps<OptionI, boolean>
) => {
  const mode = useLightMode()
  const { children } = props
  const selectProps = props.selectProps as MenuListSelectIProps

  // Use filteredOptions passed from parent component
  const { filteredOptions, value } = selectProps

  // Mirror handleSelectAll: disabled options take no part in Select All, so
  // the checkbox state must be computed over the same selectable set or the
  // rendered state contradicts what a click does
  const selectableOptions = useMemo(
    () =>
      (filteredOptions ?? []).filter(
        (opt) => !(opt as OptionI & { isDisabled?: boolean }).isDisabled
      ),
    [filteredOptions]
  )
  const totalCount = selectableOptions.length

  // Check if all selectable filtered options are selected
  const isAllSelected = useMemo(() => {
    if (totalCount === 0) return false
    const selectedValues = new Set((value || []).map((v) => v.value))
    return selectableOptions.every((opt) => selectedValues.has(opt.value))
  }, [selectableOptions, value, totalCount])

  return (
    <components.MenuList {...props}>
      {selectProps.showSelectAll && (
        <Flex
          alignItems='center'
          borderBottom='1px solid'
          borderColor={`border.input-hover.${mode}`}
          cursor='pointer'
          gap='12px'
          mb='4px'
          onClick={(e) => {
            e.stopPropagation()
            selectProps.onSelectAll()
            selectProps.onInputChange('', { action: 'set-value' })
            selectProps.onMenuClose()
          }}
          pb='8px'
          px='12px'
          py='8px'
        >
          <Checkbox
            borderColor={`text.secondary.${mode}`}
            colorScheme='checkbox-selected'
            isChecked={isAllSelected}
            pointerEvents='none'
          />
          <Text color={`text.primary.${mode}`} fontSize='14px' fontWeight='500'>
            Select All
          </Text>
        </Flex>
      )}
      {children}
    </components.MenuList>
  )
}
